import { useCallback, useEffect, useRef, useState } from 'react'

type TableScrollState = {
	containerRef: React.RefObject<HTMLDivElement | null>
	canScrollLeft: boolean
	canScrollRight: boolean
	isScrollable: boolean
	scrollLeft: () => void
	scrollRight: () => void
}

const useTableScroll = (): TableScrollState => {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const [canScrollLeft, setCanScrollLeft] = useState(false)
	const [canScrollRight, setCanScrollRight] = useState(false)
	const [isScrollable, setIsScrollable] = useState(false)

	const check = useCallback(() => {
		const el = containerRef.current
		if (!el) return
		const scrollable = el.scrollWidth > el.clientWidth
		setIsScrollable(scrollable)
		setCanScrollLeft(el.scrollLeft > 0)
		setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1)
	}, [])

	const scrollLeft = useCallback(() => {
		containerRef.current?.scrollBy({ left: -200, behavior: 'smooth' })
	}, [])

	const scrollRight = useCallback(() => {
		containerRef.current?.scrollBy({ left: 200, behavior: 'smooth' })
	}, [])

	useEffect(() => {
		const el = containerRef.current
		if (!el) return

		check()
		el.addEventListener('scroll', check)
		window.addEventListener('resize', check)
		const ro = new ResizeObserver(check)
		ro.observe(el)

		return () => {
			el.removeEventListener('scroll', check)
			window.removeEventListener('resize', check)
			ro.disconnect()
		}
	}, [check])

	return { containerRef, canScrollLeft, canScrollRight, isScrollable, scrollLeft, scrollRight }
}

export { useTableScroll }
export type { TableScrollState }
